import { setBulkProductMappingModalDisplay } from "@/features/modalSlice";
import { Button, Modal, Select, Switch, Form } from "antd";
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import BulkModalTagDropDown from "../drop-downs/tags/BulkModalTagDropDown";
import {
    IPfmSkuFilterParams,
    TClientTags,
    TProductMappingData,
    TProductMappingFormValues,
    TProductMappingUpdateConfig,
    TUpdateConfigBulkProductMapping,
    VcpmColumns,
} from "@/ts/common-types";
import { compact, find, flatten, map, pluck, some, uniq } from "underscore";
import { showToast } from "@/ts/modals";
import { internationalizeNumericValue, updateProductMapping } from "@/ts/utils";
import { mainContext } from "@/ts/constants";
import { IMainContext } from "@/ts/common-types";
import * as Sentry from "@sentry/react";

type IBulkProductMappingModalProps = {
    show: boolean;
};

const BulkProductMappingModal: React.FC<IBulkProductMappingModalProps> = ({
    show,
}) => {
    const dispatch = useDispatch();
    const { productMappingGridApi } = useContext<IMainContext>(mainContext);
    const [selectedTags, setSelectedTags] = useState([]);
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);

    const productMappingSelectionData = useSelector(
        (state: TProductMappingData) => state.productMappingSelectionData.value
    );

    const productMappingAllPagesSelected = useSelector(
        (state: { productMappingAllPagesSelected: { value: boolean } }) =>
            state.productMappingAllPagesSelected.value
    );

    const productMappingData = useSelector(
        (state: TProductMappingData) => state.productMappingData.value
    );

    const productMappingFilterModel = useSelector(
        (state: {
            productMappingFilterModel: { value: IPfmSkuFilterParams };
        }) => state.productMappingFilterModel.value
    );

    const bulkPfmMappingCount = useSelector(
        (state: { bulkPfmMappingCount: { value: number } }) =>
            state.bulkPfmMappingCount.value
    );

    const tagTypeList = useSelector(
        (state: { tagTypeList: { value: TClientTags[] } }) =>
            state.tagTypeList.value
    );

    const avlCategories = useMemo(() => {
        const categoryTag = find(
            tagTypeList,
            (tag) => tag.tag_type === VcpmColumns.CATEGORY
        );

        if (!categoryTag || !Array.isArray(categoryTag.tags_map)) return [];

        return categoryTag.tags_map.map((tagStr) => {
            const [id, name] = tagStr.split("##");
            return {
                value: id,
                label: name,
            };
        });
    }, [tagTypeList]);

    const avlSubCategories = useMemo(() => {
        const subCategoryTag = find(
            tagTypeList,
            (tag) => tag.tag_type === VcpmColumns.SUBCATEGORY
        );

        if (!subCategoryTag || !Array.isArray(subCategoryTag.tags)) return [];

        return subCategoryTag.tags_map.map((tagStr) => {
            const [id, name] = tagStr.split("##");
            return {
                value: id,
                label: name,
            };
        });
    }, [tagTypeList]);

    const avlBrands = useMemo(() => {
        const brandsTag = find(
            tagTypeList,
            (tag) => tag.tag_type === VcpmColumns.BRAND
        );

        if (!brandsTag || !Array.isArray(brandsTag.tags)) return [];

        return brandsTag.tags_map.map((tagStr) => {
            const [id, name] = tagStr.split("##");
            return {
                value: id,
                label: name,
            };
        });
    }, [tagTypeList]);

    const [skuIdInitialValue] = useState<string[]>(
        compact(
            uniq(
                flatten(pluck(productMappingSelectionData, VcpmColumns.SKU_ID))
            )
        ) as string[]
    );

    const [avlProductIds, setAvlProductIds] = useState<string[]>([]);

    useEffect(() => {
        setAvlProductIds(
            compact(
                uniq(flatten(pluck(productMappingData, VcpmColumns.SKU_ID)))
            ) as string[]
        );
    }, [productMappingSelectionData, productMappingData]);

    const handleClose = () => {
        dispatch(setBulkProductMappingModalDisplay(false));
    };

    const handleSubmit = async (values: TProductMappingFormValues) => {
        setIsLoading(true);
        values.tags = selectedTags;
        const {
            power_sku,
            sku_identifier,
            tags,
            category,
            subcategory,
            brand,
        } = values;

        if (sku_identifier && sku_identifier.length > 1) {
            showToast("danger", "Only one ID can be assigned!");
            setIsLoading(false);
        } else {
            try {
                const updateConfig: TUpdateConfigBulkProductMapping = productMappingAllPagesSelected
                    ? {
                        sku_channel: [],
                        update_params: {
                            ...(category && { catId: +category }),
                            ...(subcategory && {
                                subcatId: +subcategory,
                            }),
                            ...(brand && { brandId: +brand }),
                            ...(tags?.length && {
                                tagIds: tags.map((tag) => +tag.split("##")[0]),
                            }),
                            ...(power_sku !== undefined && {
                                isPowerSku: power_sku,
                            }),
                            ...(sku_identifier?.length && {
                                crossChannelProductId: sku_identifier[0],
                            }),
                        },
                        filterModel: productMappingFilterModel,
                    }
                    : {
                        sku_channel: map(
                            productMappingSelectionData,
                            (data) => ({
                                channel: data.channel_id as number,
                                sku: data.sku as string | number,
                            })
                        ),
                        update_params: {
                            ...(category && { catId: +category }),
                            ...(subcategory && {
                                subcatId: +subcategory,
                            }),
                            ...(brand && { brandId: +brand }),
                            ...(tags?.length && {
                                tagIds: tags.map((tag) => +tag.split("##")[0]),
                            }),
                            ...(power_sku !== undefined && {
                                isPowerSku: power_sku,
                            }),
                            ...(sku_identifier?.length && {
                                crossChannelProductId: sku_identifier[0],
                            }),
                        },
                        ...(!productMappingSelectionData.length
                            ? { filterModel: productMappingFilterModel }
                            : {}),
                    };

                const response = await updateProductMapping(updateConfig);

                if (!response.status) {
                    throw new Error(response.message);
                }
                showToast("success", response.message);

                // Refresh the grid data
                if (productMappingGridApi) {
                    productMappingGridApi.refreshServerSideStore({
                        route: [],
                        purge: true,
                    });
                }

                dispatch(setBulkProductMappingModalDisplay(false));
            } catch (err: any) {
                showToast("danger", `${err.message}`);
                Sentry.captureException(err);
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Modal
            open={show}
            centered
            title={
                <div>
                    <div
                        className="d-flex align-items-center pb-2"
                        style={{ fontSize: "1.5rem", fontWeight: "600" }}
                    >
                        Add Product Mapping
                    </div>
                    <div
                        className="d-flex align-items-center"
                        style={{
                            fontSize: "0.8rem",
                            color: "#666",
                            fontWeight: "400",
                            backgroundColor: "#f5f5f5",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            width: "fit-content",
                        }}
                    >
                        Filtered SKU Count:{" "}
                        <span style={{ marginLeft: "4px", fontWeight: "600" }}>
                            {internationalizeNumericValue(
                                productMappingAllPagesSelected
                                    ? bulkPfmMappingCount
                                    : productMappingSelectionData.length || bulkPfmMappingCount
                            )}
                        </span>
                    </div>
                </div>
            }
            onCancel={handleClose}
            footer={[
                <Button key="back" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={() => form.submit()}
                    loading={isLoading}
                >
                    Submit
                </Button>,
            ]}
            width="630px"
        >
            <Form
                form={form}
                layout="horizontal"
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
                onFinish={handleSubmit}
                initialValues={{
                    sku_identifier:
                        skuIdInitialValue.length === 1
                            ? [skuIdInitialValue[0]]
                            : [],
                    power_sku: some(
                        productMappingSelectionData,
                        (sku) => sku.power_sku as boolean
                    ),
                }}
            >
                <Form.Item
                    label={VcpmColumns.CATEGORY}
                    name={VcpmColumns.CATEGORY.toLowerCase()}
                    className="mt-3"
                >
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        placeholder="Select category"
                        options={map(avlCategories, (option) => ({
                            value: option.value,
                            label: option.label,
                        }))}
                        filterSort={(optionA, optionB) =>
                            (optionA?.label ?? "")
                                .toLowerCase()
                                .localeCompare(
                                    (optionB?.label ?? "").toLowerCase()
                                )
                        }
                    />
                </Form.Item>
                <Form.Item
                    label={VcpmColumns.SUBCATEGORY}
                    name={VcpmColumns.SUBCATEGORY.toLowerCase()}
                >
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        placeholder="Select subcategory"
                        options={map(avlSubCategories, (option) => ({
                            value: option.value,
                            label: option.label,
                        }))}
                        filterSort={(optionA, optionB) =>
                            (optionA?.label ?? "")
                                .toLowerCase()
                                .localeCompare(
                                    (optionB?.label ?? "").toLowerCase()
                                )
                        }
                    />
                </Form.Item>
                <Form.Item
                    label={VcpmColumns.BRAND}
                    name={VcpmColumns.BRAND.toLowerCase()}
                >
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        placeholder="Select brand"
                        options={map(avlBrands, (option) => ({
                            value: option.value,
                            label: option.label,
                        }))}
                        filterSort={(optionA, optionB) =>
                            (optionA?.label ?? "")
                                .toLowerCase()
                                .localeCompare(
                                    (optionB?.label ?? "").toLowerCase()
                                )
                        }
                    />
                </Form.Item>

                <Form.Item
                    label="Product Tags"
                    name={VcpmColumns.TAGS.toLowerCase()}
                >
                    <BulkModalTagDropDown setSelectedTags={setSelectedTags} selectedTags={selectedTags} />
                </Form.Item>

                <Form.Item
                    label="Power SKU"
                    name="power_sku"
                    valuePropName="checked"
                >
                    <Switch />
                </Form.Item>
                <Form.Item
                    label="Product ID"
                    name={VcpmColumns.SKU_ID.toLowerCase()}
                >
                    <Select
                        mode={"tags"}
                        placeholder="Select ID"
                        options={map(avlProductIds, (option) => ({
                            value: option,
                            label: option,
                        }))}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default BulkProductMappingModal;
